import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthService } from '../shared/auth/auth.service';
import { NavigationService } from '../shared/auth/navigation.service';
import { ForgotPasswordModalComponent } from '../forgot-password-modal/forgot-password-modal.component';
import { AppTitleService } from '../services/title.service';
import { ChangePasswordModalComponent } from '../change-password-modal/change-password-modal.component';
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent {
  loginForm: FormGroup;
  errorMessage = '';
  errorType: 'error' | 'warning' | 'info' = 'error';
  loading = false;
  showPassword = false;

  private authService = inject(AuthService);
  private navigationService = inject(NavigationService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  private titleService = inject(AppTitleService);

  constructor() {
    this.titleService.setTitle('Sign In');

    this.loginForm = this.fb.group({
      email: ['', [Validators.required]],
      password: ['', Validators.required]
    });
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    const credentials = {
      email: this.loginForm.value.email?.trim(),
      password: this.loginForm.value.password
    };

    console.log('📤 Sending login request:', {
      email: credentials.email,
      emailLength: credentials.email?.length
    });

    this.authService.login(credentials).subscribe({
      next: (response) => {
        console.log('✅ Login successful!');
        console.log('👤 User role:', response.user.role);

        this.loading = false;
        this.errorMessage = '';
if (response.user['mustChangePassword']) {
    const dialogRef = this.dialog.open(ChangePasswordModalComponent, {
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'forced-password-change-dialog',
      disableClose: true,        // ✅ blocks Escape / backdrop click
      autoFocus: true,
      data: { forced: true }     // ✅ tells the modal to hide Cancel/close
    });

    dialogRef.afterClosed().subscribe((result) => {
      // Only proceed once the password was actually changed
      if (result?.success) {
        const userRole = response.user.role;
        const triedUrl = this.authService.triedUrl;
        this.authService.triedUrl = '';
        this.navigationService.navigateToRoleDefaultPage(userRole, triedUrl);
      }
    });
    return;
  }
        const userRole = response.user.role;
        const triedUrl = this.authService.triedUrl;
        this.authService.triedUrl = '';

        console.log('🧭 Navigating based on role:', userRole);
        console.log('🔗 Attempted URL:', triedUrl);

        this.navigationService.navigateToRoleDefaultPage(userRole, triedUrl);
      },
      error: (error: any) => {
        console.error('❌ Login error:', error);
        this.loading = false;

        // Extract error message from response
        const errorMsg = this.extractErrorMessage(error);
        this.errorMessage = errorMsg.message;
        this.errorType = errorMsg.type;
      }
    });
  }
private mapErrorCode(code: string, fallbackMessage?: string): { message: string; type: 'error' | 'warning' | 'info' } {
  switch (code) {
    case 'INVALID_CREDENTIALS':
      return { message: '❌ Invalid email or password. Please try again.', type: 'error' };

    case 'ACCOUNT_PENDING':
      return { message: '⏳ Your account is pending approval. Please check back later.', type: 'warning' };

    case 'ACCOUNT_REJECTED':
      return { message: `❌ ${fallbackMessage || 'Your account registration was rejected.'}`, type: 'error' };

    case 'ACCOUNT_DEACTIVATED':
      return { message: '🔒 Your account has been deactivated. Please contact the administrator.', type: 'error' };

    case 'ACCOUNT_ARCHIVED':
      return { message: '🚫 Account Suspended. This account has been archived — contact your administrator.', type: 'error' };

    case 'ACCOUNT_NOT_APPROVED':
      return { message: '❌ Your account is not approved. Please contact the administrator.', type: 'error' };

    case 'ORG_DEACTIVATED':
    case 'ORG_ARCHIVED':
    case 'ORG_REJECTED':
    case 'ORG_PENDING':
    case 'ORG_INACTIVE':
      return { message: `⚠️ ${fallbackMessage || 'Your organization is not active. Please contact the system administrator.'}`, type: 'error' };

    default:
      return { message: `❌ ${fallbackMessage || 'Login failed. Please try again.'}`, type: 'error' };
  }
}

private extractErrorMessage(error: any): { message: string; type: 'error' | 'warning' | 'info' } {
  // Handle 401 Unauthorized
  if (error.status === 401) {
    const responseError = error.error;

    // ✅ Preferred path: backend now returns real JSON { code, message }
    if (responseError?.code) {
      return this.mapErrorCode(responseError.code, responseError.message);
    }

    // --- Legacy fallback below (kept as a safety net for any 401
    // --- that doesn't carry a code, e.g. ModelState validation errors)

    // Check if it's a string error message
    if (typeof responseError === 'string') {
      // Pending Approval
      if (responseError.toLowerCase().includes('pending approval')) {
        return {
          message: '⏳ Your account is pending approval. Please check back later.',
          type: 'warning'
        };
      }

      // Deactivated Account
      if (responseError.toLowerCase().includes('deactivated')) {
        return {
          message: '🔒 Your account has been deactivated. Please contact the administrator.',
          type: 'error'
        };
      }

      // Archived Account
      if (responseError.toLowerCase().includes('archived')) {
        return {
          message: '📦 Your account has been archived and is no longer active.',
          type: 'error'
        };
      }

      // Rejected Account
      if (responseError.toLowerCase().includes('rejected')) {
        // Extract rejection reason if provided
        const reasonMatch = responseError.match(/Reason:\s*(.+?)$/i);
        const reason = reasonMatch ? reasonMatch[1] : 'No reason provided';
        return {
          message: `❌ Your account was rejected. ${reason}`,
          type: 'error'
        };
      }

      // Invalid credentials
      if (responseError.toLowerCase().includes('invalid')) {
        return {
          message: '❌ Invalid email or password. Please try again.',
          type: 'error'
        };
      }

      // Generic string error
      return {
        message: `❌ ${responseError}`,
        type: 'error'
      };
    }

    // Check if it's an object with error property
    if (responseError?.error && typeof responseError.error === 'string') {
      return this.extractErrorMessage({
        status: 401,
        error: responseError.error
      });
    }

    // Check if it's an object with message property but no code
    if (responseError?.message && typeof responseError.message === 'string') {
      return this.extractErrorMessage({
        status: 401,
        error: responseError.message
      });
    }
  }

  // Handle other HTTP errors
  if (error.status >= 500) {
    return {
      message: '❌ Server error. Please try again later.',
      type: 'error'
    };
  }

  if (error.status === 0) {
    return {
      message: '❌ Cannot connect to server. Please check your connection.',
      type: 'error'
    };
  }

  // Default error message
  const msg = error.error?.message || error.message || 'Login failed. Please try again.';
  return {
    message: `❌ ${msg}`,
    type: 'error'
  };
}
  clearError(): void {
    this.errorMessage = '';
  }

  resetForgotPassword(): void {
    this.dialog.open(ForgotPasswordModalComponent, {
      width: '500px',
      maxWidth: '90vw',
      panelClass: 'forgot-password-dialog',
      disableClose: false,
      autoFocus: true
    });
  }
setRegistrationFlow(): void {
  sessionStorage.setItem('registrationFlow', 'patient-self-registration');
}
  signInWithGoogle(): void {
    console.log('Google sign-in clicked');
  }
}
